<template>
  <AppTable class="users-table">
    <thead>
      <tr>
        <AppTableSortableHeaderCell :field="nameSortField" :sort="model.sort">Namn</AppTableSortableHeaderCell>
        <AppTableSortableHeaderCell class="personal-number-column" :field="personalNumberSortField" :sort="model.sort">
          Personnummer
        </AppTableSortableHeaderCell>
      </tr>
    </thead>

    <tbody>
      <AppTableRow v-if="model.loading">
        <AppTableCell :colspan="columnCount.toString()" class="align-center">Hämtar användare...</AppTableCell>
      </AppTableRow>

      <template v-else-if="model.items.length">
        <AppTableRow
          v-for="user in model.items"
          :key="user.userId"
          :route-location="userRoute(user)"
          @click="emit('navigate')"
        >
          <AppTableCell>
            <div class="name-cell">
              <span>{{ formatName(user) }}</span>
              <span class="relation-badges">
                <AppChip v-for="relation in user.relations" :key="relation" :color="relationOption(relation).color">
                  {{ relationOption(relation).label }}
                </AppChip>
              </span>
            </div>
          </AppTableCell>
          <AppTableCell nowrap>{{ user.personalNumber }}</AppTableCell>
        </AppTableRow>
      </template>

      <AppTableRow v-else>
        <AppTableCell :colspan="columnCount.toString()" class="align-center">{{ model.emptyMessage }}</AppTableCell>
      </AppTableRow>
    </tbody>
  </AppTable>
</template>

<script setup lang="ts">
import { AppChip, AppTable, AppTableCell, AppTableRow, AppTableSortableHeaderCell } from '@staff/ui'

import type { UserListSortField, UsersTableModel } from '@/composables/userSearch'
import type { UserRelation, UserSearchResultItem } from '@/services/user'

import { USER_RELATION_OPTIONS } from '../userRelations'

defineProps<{
  model: UsersTableModel
}>()

const emit = defineEmits<{
  (e: 'navigate'): void
}>()

const columnCount = 2
const nameSortField: UserListSortField = 'name'
const personalNumberSortField: UserListSortField = 'personalNumber'

const userRoute = (user: UserSearchResultItem) => ({ name: 'kund', params: { id: user.userId } })

const formatName = (user: UserSearchResultItem) => [user.givenName, user.surname].filter(Boolean).join(' ') || '-'

const relationOption = (relation: UserRelation) =>
  USER_RELATION_OPTIONS.find((option) => option.value === relation) ?? USER_RELATION_OPTIONS[0]
</script>

<style scoped lang="scss">
// AppTable's root is a wrapper div, so target the inner <table>. Fixed layout makes
// column widths come from the header row alone - they don't jump when the
// loading/empty colspan row is replaced by results.
.users-table :deep(table) {
  table-layout: fixed;
}

// Personnummer has a known constant width; lock it and let the name column take the rest.
.personal-number-column {
  width: 180px;
}

.align-center {
  text-align: center;

  :deep(> a) {
    justify-content: center;
  }
}

.name-cell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.relation-badges {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
